package one.healthium.data.user.mapper

import dev.gitlive.firebase.firestore.Timestamp
import one.healthium.data.user.dto.AdditionalUserInfoDto
import one.healthium.data.user.dto.FoodPhotoQuotaDto
import one.healthium.data.user.dto.FoodLogDto
import one.healthium.data.user.dto.HealthLogAnswerDto
import one.healthium.data.user.dto.HealthLogDto
import one.healthium.data.user.dto.HealthLogEntryDto
import one.healthium.data.user.dto.PeriodLogDto
import one.healthium.data.user.dto.SymptomDto
import one.healthium.data.user.dto.SymptomLogDto
import one.healthium.data.user.dto.UserDto
import one.healthium.data.user.dto.UserProfileDto
import one.healthium.data.user.dto.UserPurchasesDto
import one.healthium.data.user.dto.UserSystemDto
import one.healthium.data.user.dto.WaterLogDto
import one.healthium.data.user.dto.WeightLogDto
import one.healthium.data.user.utils.toInstant
import one.healthium.domain.health_log.models.HealthLogAnsModel
import one.healthium.domain.health_log.models.HealthLogEntryModel
import one.healthium.domain.health_log.models.HealthLogModel
import one.healthium.domain.health_log.models.HealthLogOption
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.models.AdditionalUserInfoModel
import one.healthium.domain.user.models.FoodPhotoQuotaModel
import one.healthium.domain.user.models.FoodLogModel
import one.healthium.domain.user.models.Gender
import one.healthium.domain.user.models.PeriodLogModel
import one.healthium.domain.user.models.SubscriptionStatus
import one.healthium.domain.user.models.SymptomKind
import one.healthium.domain.user.models.SymptomModel
import one.healthium.domain.user.models.SymptomSeverity
import one.healthium.domain.user.models.SymptomsLogModel
import one.healthium.domain.user.models.SymptomsLogOptions
import one.healthium.domain.user.models.UserProfileModel
import one.healthium.domain.user.models.UserProfileOptions
import one.healthium.domain.user.models.UserPurchasesModel
import one.healthium.domain.user.models.UserSystemModel
import one.healthium.domain.user.models.WaterLogModel
import one.healthium.domain.user.models.WeightLogModel
import kotlin.uuid.Uuid

internal class UserMapper(
    private val foodLogMapper: FoodLogMapper,
) {

    fun mapUserDto(
        userId: String,
        dto: UserDto,
        foodPhotoQuota: FoodPhotoQuotaDto?,
        waterLogs: List<WaterLogDto>,
        weightLogs: List<WeightLogDto>,
        foodLogs: List<FoodLogDto>,
        periodLog: PeriodLogDto,
        symptomLogs: List<SymptomLogDto>,
        healthLog: List<HealthLogDto>,
    ): UserModel {
        return UserModel(
            id = userId,
            profile = mapProfileDto(dto.profile),
            purchases = mapPurchasesDto(dto.purchases),
            system = mapSystemDto(dto.system, foodPhotoQuota),
            waterLogs = waterLogs.map(::mapWaterLog),
            weightLogs = weightLogs.map(::mapWeightLog),
            foodLogs = foodLogs.map(::mapFoodLog),
            periodLogs = mapPeriodLog(periodLog),
            symptomsLogs = symptomLogs.map(::mapSymptomLog),
            healthLogs = healthLog.map(::mapHealthLog),
        )
    }

    fun mapUserModel(model: UserModel): UserDto {
        return UserDto(
            profile = mapProfileModel(model.profile)
        )
    }

    fun mapProfileModel(model: UserProfileModel): UserProfileDto {
        return UserProfileDto(
            name = model.name,
            age = model.age,
            weight = model.weight,
            targetWeight = model.targetWeight,
            height = model.height,
            gender = model.gender?.key,
            additionalInfo = model.additionalInfo.mapValues { (_, value) ->
                value.map { selectableOption ->
                    AdditionalUserInfoDto(
                        key = selectableOption.key,
                        text = selectableOption.text,
                        altText = selectableOption.altText,
                    )
                }
            },
            additionalText = model.additionalText,
            statements = model.statements,
        )
    }

    fun mapProfileOptions(options: UserProfileOptions): UserProfileDto {
        return UserProfileDto(
            name = options.name,
            age = options.age,
            weight = options.weight,
            targetWeight = options.targetWeight,
            height = options.height,
            avatarUrl = options.avatarUrl,
            additionalInfo = options.additionalInfo?.mapValues { (_, value) ->
                value.map { additionalInfoModel ->
                    AdditionalUserInfoDto(
                        key = additionalInfoModel.key,
                        text = additionalInfoModel.text,
                        altText = additionalInfoModel.altText,
                    )
                }
            },
        )
    }

    private fun mapProfileDto(dto: UserProfileDto?): UserProfileModel {
        if (dto == null) return UserProfileModel.EMPTY

        return UserProfileModel(
            name = dto.name,
            age = dto.age,
            weight = dto.weight,
            targetWeight = dto.targetWeight,
            height = dto.height,
            gender = Gender.fromKey(dto.gender),
            email = dto.email,
            avatarUrl = dto.avatarUrl,
            additionalInfo = dto.additionalInfo?.mapValues { (_, value) ->
                value.map { additionalInfoDto ->
                    AdditionalUserInfoModel(
                        key = additionalInfoDto.key,
                        text = additionalInfoDto.text.orEmpty(),
                        altText = additionalInfoDto.altText,
                    )
                }
            }.orEmpty(),
            additionalText = dto.additionalText.orEmpty(),
            statements = dto.statements.orEmpty(),
        )
    }

    private fun mapPurchasesDto(dto: UserPurchasesDto?): UserPurchasesModel {
        if (dto == null) {
            return UserPurchasesModel(
                subscription = SubscriptionStatus.Inactive,
                purchaseType = null,
                subscriptionId = null,
            )
        }

        return UserPurchasesModel(
            subscription = when (dto.subscription) {
                "active" -> SubscriptionStatus.Active
                "inactive" -> SubscriptionStatus.Inactive
                else -> SubscriptionStatus.Inactive
            },
            purchaseType = dto.purchaseType,
            subscriptionId = dto.subscriptionId,
        )
    }

    internal fun mapSystemDto(dto: UserSystemDto?, foodPhotoQuota: FoodPhotoQuotaDto?): UserSystemModel {
        if (dto == null) {
            return UserSystemModel(
                playTime = 0,
                foodPhotoQuota = mapFoodPhotoQuotaDto(foodPhotoQuota),
            )
        }

        return UserSystemModel(
            playTime = dto.playTime,
            foodPhotoQuota = mapFoodPhotoQuotaDto(foodPhotoQuota),
        )
    }

    internal fun mapSystemModel(model: UserSystemModel): UserSystemDto {
        return UserSystemDto(
            playTime = model.playTime,
        )
    }

    private fun mapFoodPhotoQuotaDto(dto: FoodPhotoQuotaDto?): FoodPhotoQuotaModel? {
        val limit = dto?.limit ?: return null
        val remaining = dto.remaining ?: return null
        val resetAt = dto.resetAt?.toInstant() ?: return null

        return FoodPhotoQuotaModel(
            limit = limit,
            remaining = remaining,
            resetAt = resetAt,
        )
    }

    private fun mapWaterLog(dto: WaterLogDto) = WaterLogModel(
        value = dto.water,
        date = dto.date.toInstant(),
    )

    private fun mapWeightLog(dto: WeightLogDto) = WeightLogModel(
        value = dto.weight,
        date = dto.date.toInstant(),
    )

    private fun mapFoodLog(dto: FoodLogDto) = FoodLogModel(
        id = dto.id,
        date = dto.date.toInstant(),
        food = dto.food.map { foodLogMapper.dtoToModel(it) }
    )

    private fun mapPeriodLog(dto: PeriodLogDto) = PeriodLogModel(
        periods = dto.periods.map { it.toInstant() }
    )

    private fun mapSymptomLog(dto: SymptomLogDto) = SymptomsLogModel(
        id = dto.id,
        date = dto.date.toInstant(),
        symptoms = dto.symptoms.map { symptomDto ->
            SymptomModel(
                kind = when (symptomDto.kind) {
                    SymptomKind.Bloating.toKey() -> SymptomKind.Bloating
                    SymptomKind.ExcessGas.toKey() -> SymptomKind.ExcessGas
                    SymptomKind.Constipation.toKey() -> SymptomKind.Constipation
                    else -> SymptomKind.Unknown
                },
                severity = when (symptomDto.severity) {
                    SymptomSeverity.Good.toKey() -> SymptomSeverity.Good
                    SymptomSeverity.Neutral.toKey() -> SymptomSeverity.Neutral
                    SymptomSeverity.Bad.toKey() -> SymptomSeverity.Bad
                    else -> SymptomSeverity.Neutral
                },
            )
        }
    )

    fun mapSymptomOptions(options: SymptomsLogOptions): SymptomLogDto {
        return SymptomLogDto(
            id = Uuid.random().toString(),
            date = Timestamp.now(),
            symptoms = options.symptoms.map { symptom ->
                SymptomDto(
                    kind = symptom.kind.toKey(),
                    severity = symptom.severity.toKey()
                )
            }
        )
    }

    fun mapHealthLogOptions(options: List<HealthLogOption>): HealthLogDto {
        return HealthLogDto(
            id = Uuid.random().toString(),
            date = Timestamp.now(),
            answers = options.map { option ->
                HealthLogEntryDto(
                    questionId = option.questionId,
                    answers = option.answers.map { answer ->
                        HealthLogAnswerDto(
                            answerId = answer.answerId,
                            weight = answer.weight
                        )
                    }
                )
            }
        )
    }

    fun mapHealthLog(dto: HealthLogDto): HealthLogModel {
        return HealthLogModel(
            id = dto.id,
            date = dto.date.toInstant(),
            answers = dto.answers.map { answerDto ->
                HealthLogEntryModel(
                    questionId = answerDto.questionId,
                    answers = answerDto.answers.map { answer ->
                        HealthLogAnsModel(
                            answerId = answer.answerId,
                            weight = answer.weight
                        )
                    }
                )
            }
        )
    }
}

private fun SymptomKind.toKey() = when (this) {
    SymptomKind.Bloating -> "Bloating"
    SymptomKind.ExcessGas -> "ExcessGas"
    SymptomKind.Constipation -> "Constipation"
    SymptomKind.Unknown -> "Unknown"
}

private fun SymptomSeverity.toKey() = when (this) {
    SymptomSeverity.Good -> "good"
    SymptomSeverity.Neutral -> "neutral"
    SymptomSeverity.Bad -> "bad"
}
